import React, { useState, useEffect } from 'react';
import api from '../../api';
import './TeamsAndBoardsPage.css';

export default function TeamsAndBoardsPage() {
  const [teams, setTeams] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showBoardForm, setShowBoardForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingBoard, setEditingBoard] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', description: '' });
  const [boardForm, setBoardForm] = useState({ team_id: '', name: '', description: '' });
  const [managingBoard, setManagingBoard] = useState(null);
  const [boardTypes, setBoardTypes] = useState([]);
  const [boardBrands, setBoardBrands] = useState([]);
  const [boardStages, setBoardStages] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  const [editingType, setEditingType] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [newStageName, setNewStageName] = useState('');
  const [newStageStatusId, setNewStageStatusId] = useState('');
  const [boardSettings, setBoardSettings] = useState({ 
    burndown_chart_enabled: false,
    visible_columns: ['title', 'type', 'priority', 'status', 'requester', 'team']
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsRes, boardsRes] = await Promise.all([
        api.get('/teams'),
        api.get('/boards')
      ]);
      setTeams(teamsRes.data || []);
      setBoards(boardsRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      alert('Failed to load teams and boards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await api.post('/teams', teamForm);
      setTeamForm({ name: '', description: '' });
      setShowTeamForm(false);
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create team. Please try again.';
      alert(errorMsg);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/teams/${editingTeam.id}`, teamForm);
      setEditingTeam(null);
      setTeamForm({ name: '', description: '' });
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update team. Please try again.';
      alert(errorMsg);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This will also delete all associated boards and cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/teams/${teamId}`);
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete team. Please try again.';
      alert(errorMsg);
    }
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({ name: team.name, description: team.description || '' });
    setShowTeamForm(false);
  };

  const handleCancelEditTeam = () => {
    setEditingTeam(null);
    setTeamForm({ name: '', description: '' });
  };

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    if (!boardForm.team_id) {
      alert('Please select a team.');
      return;
    }
    try {
      await api.post('/boards', boardForm);
      setBoardForm({ team_id: '', name: '', description: '' });
      setShowBoardForm(false);
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create board. Please try again.';
      alert(errorMsg);
    }
  };

  const handleUpdateBoard = async (e) => {
    e.preventDefault();
    if (!boardForm.team_id) {
      alert('Please select a team.');
      return;
    }
    try {
      await api.put(`/boards/${editingBoard.id}`, boardForm);
      setEditingBoard(null);
      setBoardForm({ team_id: '', name: '', description: '' });
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update board. Please try again.';
      alert(errorMsg);
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (!window.confirm('Are you sure you want to delete this board? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/boards/${boardId}`);
      await loadData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete board. Please try again.';
      alert(errorMsg);
    }
  };

  const handleEditBoard = (board) => {
    setEditingBoard(board);
    setBoardForm({ 
      team_id: board.team_id.toString(), 
      name: board.name, 
      description: board.description || '' 
    });
    setShowBoardForm(false);
  };

  const handleCancelEditBoard = () => {
    setEditingBoard(null);
    setBoardForm({ team_id: '', name: '', description: '' });
  };

  const handleManageBoard = async (board) => {
    setManagingBoard(board);
    setEditingType(null);
    setEditingBrand(null);
    setNewTypeName('');
    setNewBrandName('');
    await loadBoardSettings(board.id);
  };

  const loadBoardSettings = async (boardId) => {
    try {
      const [typesRes, brandsRes, stagesRes, statusesRes, settingsRes] = await Promise.all([
        api.get(`/board-settings/${boardId}/types`),
        api.get(`/board-settings/${boardId}/brands`),
        api.get(`/board-settings/${boardId}/stages`),
        api.get('/tasks/statuses'),
        api.get(`/board-settings/${boardId}/settings`)
      ]);
      setBoardTypes(typesRes.data || []);
      setBoardBrands(brandsRes.data || []);
      setBoardStages(stagesRes.data || []);
      setAllStatuses(statusesRes.data || []);
      setBoardSettings(settingsRes.data || { 
        burndown_chart_enabled: false,
        visible_columns: ['title', 'type', 'priority', 'status', 'requester', 'team']
      });
    } catch (err) {
      console.error('Failed to load board settings:', err);
      alert('Failed to load board settings. Please try again.');
    }
  };

  const handleToggleBurndownChart = async (enabled) => {
    if (!managingBoard) return;
    
    try {
      const response = await api.put(`/board-settings/${managingBoard.id}/settings`, {
        burndown_chart_enabled: enabled
      });
      setBoardSettings(response.data);
    } catch (err) {
      console.error('Failed to update burndown chart setting:', err);
      alert('Failed to update setting. Please try again.');
    }
  };

  const handleToggleVisibleColumn = async (columnName) => {
    if (!managingBoard) return;
    
    const currentColumns = boardSettings.visible_columns || ['title', 'type', 'priority', 'status', 'requester', 'team'];
    const newColumns = currentColumns.includes(columnName)
      ? currentColumns.filter(col => col !== columnName)
      : [...currentColumns, columnName];
    
    try {
      const response = await api.put(`/board-settings/${managingBoard.id}/settings`, {
        visible_columns: newColumns
      });
      setBoardSettings(response.data);
    } catch (err) {
      console.error('Failed to update visible columns:', err);
      alert('Failed to update visible columns. Please try again.');
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) {
      alert('Please enter a type name.');
      return;
    }
    try {
      await api.post(`/board-settings/${managingBoard.id}/types`, { type: newTypeName.trim() });
      setNewTypeName('');
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create type. Please try again.';
      alert(errorMsg);
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) {
      alert('Please enter a type name.');
      return;
    }
    try {
      await api.put(`/board-settings/${managingBoard.id}/types/${editingType.id}`, { type: newTypeName.trim() });
      setEditingType(null);
      setNewTypeName('');
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update type. Please try again.';
      alert(errorMsg);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this type? This cannot be undone if there are tasks using it.')) {
      return;
    }
    try {
      await api.delete(`/board-settings/${managingBoard.id}/types/${typeId}`);
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete type. Please try again.';
      alert(errorMsg);
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setNewTypeName(type.type);
  };

  const handleCancelEditType = () => {
    setEditingType(null);
    setNewTypeName('');
  };

  const handleMoveType = async (typeId, direction) => {
    if (!managingBoard) return;
    
    const currentTypes = [...boardTypes];
    const currentIndex = currentTypes.findIndex(t => t.id === typeId);
    
    if (currentIndex === -1) return;
    
    // Calculate new index
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check bounds
    if (newIndex < 0 || newIndex >= currentTypes.length) return;
    
    // Swap items
    [currentTypes[currentIndex], currentTypes[newIndex]] = [currentTypes[newIndex], currentTypes[currentIndex]];
    
    // Get the new order of IDs (ensure they're integers)
    const typeIds = currentTypes.map(t => parseInt(t.id, 10));
    
    try {
      console.log('Reordering types with IDs:', typeIds);
      const response = await api.put(`/board-settings/${managingBoard.id}/types/order`, { typeIds });
      setBoardTypes(response.data || []);
    } catch (err) {
      console.error('Failed to reorder types:', err);
      const errorMsg = err.response?.data?.error || 'Failed to reorder types. Please try again.';
      console.error('Error details:', err.response?.data);
      alert(errorMsg);
    }
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    if (!newBrandName.trim()) {
      alert('Please enter a brand name.');
      return;
    }
    try {
      await api.post(`/board-settings/${managingBoard.id}/brands`, { name: newBrandName.trim() });
      setNewBrandName('');
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create brand. Please try again.';
      alert(errorMsg);
    }
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    if (!newBrandName.trim()) {
      alert('Please enter a brand name.');
      return;
    }
    try {
      await api.put(`/board-settings/${managingBoard.id}/brands/${editingBrand.id}`, { name: newBrandName.trim() });
      setEditingBrand(null);
      setNewBrandName('');
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update brand. Please try again.';
      alert(errorMsg);
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand? This cannot be undone if there are tasks using it.')) {
      return;
    }
    try {
      await api.delete(`/board-settings/${managingBoard.id}/brands/${brandId}`);
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete brand. Please try again.';
      alert(errorMsg);
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setNewBrandName(brand.name);
  };

  const handleCancelEditBrand = () => {
    setEditingBrand(null);
    setNewBrandName('');
  };

  const handleCreateStage = async (e) => {
    e.preventDefault();
    if (!newStageName.trim()) {
      alert('Please enter a stage name.');
      return;
    }
    try {
      await api.post(`/board-settings/${managingBoard.id}/stages`, { 
        stage_name: newStageName.trim(),
        status_id: newStageStatusId || null
      });
      setNewStageName('');
      setNewStageStatusId('');
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to create stage. Please try again.';
      alert(errorMsg);
    }
  };

  const handleUpdateStage = async (e) => {
    e.preventDefault();
    if (!newStageName.trim()) {
      alert('Please enter a stage name.');
      return;
    }
    try {
      await api.put(`/board-settings/${managingBoard.id}/stages/${editingStage.id}`, { 
        stage_name: newStageName.trim(),
        status_id: newStageStatusId || null
      });
      setEditingStage(null);
      setNewStageName('');
      setNewStageStatusId('');
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update stage. Please try again.';
      alert(errorMsg);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Are you sure you want to delete this stage? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/board-settings/${managingBoard.id}/stages/${stageId}`);
      await loadBoardSettings(managingBoard.id);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete stage. Please try again.';
      alert(errorMsg);
    }
  };

  const handleEditStage = (stage) => {
    setEditingStage(stage);
    setNewStageName(stage.stage_name);
    setNewStageStatusId(stage.status_id || '');
  };

  const handleCancelEditStage = () => {
    setEditingStage(null);
    setNewStageName('');
    setNewStageStatusId('');
  };

  const handleMoveStage = async (stageId, direction) => {
    if (!managingBoard) return;
    
    const currentStages = [...boardStages];
    const currentIndex = currentStages.findIndex(s => s.id === stageId);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= currentStages.length) return;
    
    [currentStages[currentIndex], currentStages[newIndex]] = [currentStages[newIndex], currentStages[currentIndex]];
    
    const stageIds = currentStages.map(s => parseInt(s.id, 10));
    
    try {
      console.log('Reordering stages with IDs:', stageIds);
      const response = await api.put(`/board-settings/${managingBoard.id}/stages/order`, { stageIds });
      setBoardStages(response.data || []);
    } catch (err) {
      console.error('Failed to reorder stages:', err);
      const errorMsg = err.response?.data?.error || 'Failed to reorder stages. Please try again.';
      console.error('Error details:', err.response?.data);
      alert(errorMsg);
    }
  };

  if (loading) {
    return <div className="teams-boards-page loading">Loading...</div>;
  }

  return (
    <div className="teams-boards-page">
      <h2>Teams & Boards Management</h2>
      
      <div className="teams-boards-container">
        {/* Teams Section */}
        <div className="section">
          <div className="section-header">
            <h3>Teams</h3>
            {!showTeamForm && !editingTeam && (
              <button 
                className="btn-primary" 
                onClick={() => {
                  setShowTeamForm(true);
                  setEditingTeam(null);
                  setTeamForm({ name: '', description: '' });
                }}
              >
                + Add Team
              </button>
            )}
          </div>

          {showTeamForm && !editingTeam && (
            <form className="form" onSubmit={handleCreateTeam}>
              <div className="form-field">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  required
                  placeholder="e.g., Development, Design"
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={teamForm.description}
                  onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                  rows="2"
                  placeholder="Optional description"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Create Team</button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowTeamForm(false);
                    setTeamForm({ name: '', description: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {editingTeam && (
            <form className="form" onSubmit={handleUpdateTeam}>
              <div className="form-field">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={teamForm.description}
                  onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                  rows="2"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Update Team</button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleCancelEditTeam}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="list">
            {teams.length === 0 ? (
              <div className="empty-state">No teams yet. Create your first team above.</div>
            ) : (
              teams.map(team => (
                <div key={team.id} className="list-item">
                  <div className="list-item-content">
                    <div className="list-item-name">{team.name}</div>
                    {team.description && (
                      <div className="list-item-description">{team.description}</div>
                    )}
                  </div>
                  <div className="list-item-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEditTeam(team)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteTeam(team.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Boards Section */}
        <div className="section">
          <div className="section-header">
            <h3>Boards</h3>
            {!showBoardForm && !editingBoard && (
              <button 
                className="btn-primary" 
                onClick={() => {
                  setShowBoardForm(true);
                  setEditingBoard(null);
                  setBoardForm({ team_id: '', name: '', description: '' });
                }}
                disabled={teams.length === 0}
              >
                + Add Board
              </button>
            )}
          </div>

          {teams.length === 0 && (
            <div className="info-message">
              Create at least one team before adding boards.
            </div>
          )}

          {showBoardForm && !editingBoard && (
            <form className="form" onSubmit={handleCreateBoard}>
              <div className="form-field">
                <label>Team *</label>
                <select
                  value={boardForm.team_id}
                  onChange={(e) => setBoardForm({ ...boardForm, team_id: e.target.value })}
                  required
                >
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Board Name *</label>
                <input
                  type="text"
                  value={boardForm.name}
                  onChange={(e) => setBoardForm({ ...boardForm, name: e.target.value })}
                  required
                  placeholder="e.g., Development Board, Sprint Board"
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={boardForm.description}
                  onChange={(e) => setBoardForm({ ...boardForm, description: e.target.value })}
                  rows="2"
                  placeholder="Optional description"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Create Board</button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowBoardForm(false);
                    setBoardForm({ team_id: '', name: '', description: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {editingBoard && (
            <form className="form" onSubmit={handleUpdateBoard}>
              <div className="form-field">
                <label>Team *</label>
                <select
                  value={boardForm.team_id}
                  onChange={(e) => setBoardForm({ ...boardForm, team_id: e.target.value })}
                  required
                >
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Board Name *</label>
                <input
                  type="text"
                  value={boardForm.name}
                  onChange={(e) => setBoardForm({ ...boardForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={boardForm.description}
                  onChange={(e) => setBoardForm({ ...boardForm, description: e.target.value })}
                  rows="2"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Update Board</button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={handleCancelEditBoard}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="list">
            {boards.length === 0 ? (
              <div className="empty-state">No boards yet. Create your first board above.</div>
            ) : (
              boards.map(board => (
                <div key={board.id} className="list-item">
                  <div className="list-item-content">
                    <div className="list-item-name">{board.name}</div>
                    <div className="list-item-meta">
                      Team: {board.team_name || 'Unknown'}
                    </div>
                    {board.description && (
                      <div className="list-item-description">{board.description}</div>
                    )}
                  </div>
                  <div className="list-item-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleManageBoard(board)}
                    >
                      Manage Settings
                    </button>
                    <button 
                      className="btn-edit"
                      onClick={() => handleEditBoard(board)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteBoard(board.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Board Settings Modal */}
      {managingBoard && (
        <div className="board-settings-modal-overlay" onClick={() => setManagingBoard(null)}>
          <div className="board-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="board-settings-header">
              <h3>Board Settings: {managingBoard.name}</h3>
              <button className="close-btn" onClick={() => setManagingBoard(null)}>×</button>
            </div>
            
            <div className="board-settings-content">
              {/* Board Display Options Section */}
              <div className="board-settings-section">
                <h4>Display Options</h4>
                <div className="board-settings-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={boardSettings.burndown_chart_enabled || false}
                      onChange={(e) => handleToggleBurndownChart(e.target.checked)}
                    />
                    <span>Enable Burndown Chart View (Azure DevOps style)</span>
                  </label>
                  <p className="setting-description">
                    When enabled, tasks will be displayed in a burndown chart format similar to Azure DevOps, 
                    showing progress over time with remaining work visualization.
                  </p>
                </div>
                
                <div className="board-settings-option" style={{ marginTop: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.75rem', color: '#fff', fontSize: '1rem', fontWeight: '500' }}>
                    Visible Columns
                  </label>
                  <p className="setting-description" style={{ marginBottom: '0.75rem' }}>
                    Select which columns should be displayed in the workboard table view.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingLeft: '1.75rem' }}>
                    {['title', 'type', 'priority', 'status', 'requester', 'team', 'sprint'].map((column) => {
                      const isChecked = (boardSettings.visible_columns || ['title', 'type', 'priority', 'status', 'requester', 'team']).includes(column);
                      return (
                        <label key={column} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleVisibleColumn(column)}
                          />
                          <span style={{ textTransform: 'capitalize' }}>{column}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Task Types Section */}
              <div className="board-settings-section">
                <h4>Task Types</h4>
                
                {!editingType && (
                  <form className="board-settings-form" onSubmit={handleCreateType}>
                    <div className="form-row">
                      <input
                        type="text"
                        value={newTypeName}
                        onChange={(e) => setNewTypeName(e.target.value)}
                        placeholder="Enter type name"
                        required
                      />
                      <button type="submit" className="btn-primary">Add Type</button>
                    </div>
                  </form>
                )}

                {editingType && (
                  <form className="board-settings-form" onSubmit={handleUpdateType}>
                    <div className="form-row">
                      <input
                        type="text"
                        value={newTypeName}
                        onChange={(e) => setNewTypeName(e.target.value)}
                        placeholder="Enter type name"
                        required
                      />
                      <button type="submit" className="btn-primary">Update</button>
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={handleCancelEditType}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                <div className="board-settings-list">
                  {boardTypes.length === 0 ? (
                    <div className="empty-state">No types yet. Add your first type above.</div>
                  ) : (
                    boardTypes.map((type, index) => (
                      <div key={type.id} className="board-settings-item">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <button
                              className="btn-move"
                              onClick={() => handleMoveType(type.id, 'up')}
                              disabled={index === 0}
                              title="Move up"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: index === 0 ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                                cursor: index === 0 ? 'not-allowed' : 'pointer',
                                padding: '0.25rem',
                                fontSize: '0.8rem',
                                lineHeight: 1
                              }}
                            >
                              ▲
                            </button>
                            <button
                              className="btn-move"
                              onClick={() => handleMoveType(type.id, 'down')}
                              disabled={index === boardTypes.length - 1}
                              title="Move down"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: index === boardTypes.length - 1 ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                                cursor: index === boardTypes.length - 1 ? 'not-allowed' : 'pointer',
                                padding: '0.25rem',
                                fontSize: '0.8rem',
                                lineHeight: 1
                              }}
                            >
                              ▼
                            </button>
                          </div>
                          <span>{type.type}</span>
                        </div>
                        <div className="board-settings-item-actions">
                          <button 
                            className="btn-edit"
                            onClick={() => handleEditType(type)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDeleteType(type.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Brands Section */}
              <div className="board-settings-section">
                <h4>Brands</h4>
                
                {!editingBrand && (
                  <form className="board-settings-form" onSubmit={handleCreateBrand}>
                    <div className="form-row">
                      <input
                        type="text"
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        placeholder="Enter brand name"
                        required
                      />
                      <button type="submit" className="btn-primary">Add Brand</button>
                    </div>
                  </form>
                )}

                {editingBrand && (
                  <form className="board-settings-form" onSubmit={handleUpdateBrand}>
                    <div className="form-row">
                      <input
                        type="text"
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        placeholder="Enter brand name"
                        required
                      />
                      <button type="submit" className="btn-primary">Update</button>
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={handleCancelEditBrand}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                <div className="board-settings-list">
                  {boardBrands.length === 0 ? (
                    <div className="empty-state">No brands yet. Add your first brand above.</div>
                  ) : (
                    boardBrands.map(brand => (
                      <div key={brand.id} className="board-settings-item">
                        <span>{brand.name}</span>
                        <div className="board-settings-item-actions">
                          <button 
                            className="btn-edit"
                            onClick={() => handleEditBrand(brand)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDeleteBrand(brand.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Stages Section */}
              <div className="board-settings-section">
                <h4>Stages</h4>
                <p className="setting-description" style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                  Stages are board-specific workflow steps. Each stage can be mapped to a status for reporting consistency.
                </p>
                
                {!editingStage && (
                  <form className="board-settings-form" onSubmit={handleCreateStage}>
                    <div className="form-row">
                      <input
                        type="text"
                        value={newStageName}
                        onChange={(e) => setNewStageName(e.target.value)}
                        placeholder="Enter stage name"
                        required
                        style={{ flex: 1 }}
                      />
                      <select
                        value={newStageStatusId}
                        onChange={(e) => setNewStageStatusId(e.target.value)}
                        style={{ flex: 1, padding: '0.6rem', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '4px', background: 'rgba(255, 255, 255, 0.1)', color: '#fff', fontSize: '0.9rem' }}
                      >
                        <option value="">No Status Mapping (Optional)</option>
                        {allStatuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.status}
                          </option>
                        ))}
                      </select>
                      <button type="submit" className="btn-primary">Add Stage</button>
                    </div>
                  </form>
                )}

                {editingStage && (
                  <form className="board-settings-form" onSubmit={handleUpdateStage}>
                    <div className="form-row">
                      <input
                        type="text"
                        value={newStageName}
                        onChange={(e) => setNewStageName(e.target.value)}
                        placeholder="Enter stage name"
                        required
                        style={{ flex: 1 }}
                      />
                      <select
                        value={newStageStatusId}
                        onChange={(e) => setNewStageStatusId(e.target.value)}
                        style={{ flex: 1, padding: '0.6rem', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '4px', background: 'rgba(255, 255, 255, 0.1)', color: '#fff', fontSize: '0.9rem' }}
                      >
                        <option value="">No Status Mapping (Optional)</option>
                        {allStatuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.status}
                          </option>
                        ))}
                      </select>
                      <button type="submit" className="btn-primary">Update</button>
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={handleCancelEditStage}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                <div className="board-settings-list">
                  {boardStages.length === 0 ? (
                    <div className="empty-state">No stages yet. Add your first stage above.</div>
                  ) : (
                    boardStages.map((stage, index) => (
                      <div key={stage.id} className="board-settings-item">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <button
                              className="btn-move"
                              onClick={() => handleMoveStage(stage.id, 'up')}
                              disabled={index === 0}
                              title="Move up"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: index === 0 ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                                cursor: index === 0 ? 'not-allowed' : 'pointer',
                                padding: '0.25rem',
                                fontSize: '0.8rem',
                                lineHeight: 1
                              }}
                            >
                              ▲
                            </button>
                            <button
                              className="btn-move"
                              onClick={() => handleMoveStage(stage.id, 'down')}
                              disabled={index === boardStages.length - 1}
                              title="Move down"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: index === boardStages.length - 1 ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                                cursor: index === boardStages.length - 1 ? 'not-allowed' : 'pointer',
                                padding: '0.25rem',
                                fontSize: '0.8rem',
                                lineHeight: 1
                              }}
                            >
                              ▼
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                            <span>{stage.stage_name}</span>
                            {stage.status && (
                              <span style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                                Maps to: {stage.status}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="board-settings-item-actions">
                          <button 
                            className="btn-edit"
                            onClick={() => handleEditStage(stage)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDeleteStage(stage.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

